import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import capi, { mediaFull } from "@/lib/customerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, Star, MapPin, Briefcase, Search, Loader2, Layers, BadgeCheck, ArrowRight, Building2,
} from "lucide-react";

const initials = (s) => (s || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function Partners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ category: "all", city: "all", subcategory: "" });
  const [q, setQ] = useState("");

  useEffect(() => {
    Promise.all([capi.get("/partner/categories"), capi.get("/partner/public/cities")])
      .then(([c, ci]) => { setCats(c.data); setCities(ci.data); }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.category !== "all") params.category = f.category;
      if (f.city !== "all") params.city = f.city;
      if (f.subcategory.trim()) params.subcategory = f.subcategory.trim();
      const { data } = await capi.get("/partner/public/directory", { params });
      setPartners(data);
    } catch { setPartners([]); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const shown = partners.filter((p) => !q || p.company_name.toLowerCase().includes(q.toLowerCase()));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <section className="hero-wash border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 mb-6 shadow-sm">
            <ShieldCheck className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} />
            <span className="text-xs font-semibold tracking-wide text-slate-600">Nirmantra Verified Partner Network</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Find your construction partner</h1>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">Discover verified contractors, architects, designers and specialists — evaluated by Nirmantra. Your number stays private until you choose to connect.</p>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="partners-search" className="rounded-xl pl-9" placeholder="Search partner name…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="filter-city" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={f.city} onChange={(e) => set("city", e.target.value)}>
            <option value="all">All Cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-category" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm max-w-[220px]" value={f.category} onChange={(e) => set("category", e.target.value)}>
            <option value="all">All Categories</option>
            {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <Input data-testid="filter-subcategory" className="rounded-xl w-40" placeholder="Sub-category" value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)} />
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <p className="text-sm text-slate-500 mb-5" data-testid="partners-count">{loading ? "Loading partners…" : `${shown.length} verified partner${shown.length === 1 ? "" : "s"}`}</p>
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-24 text-slate-500"><Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />No partners match your filters yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
            {shown.map((p) => (
              <div key={p.id} data-testid={`partner-card-${p.id}`} className="card-premium rounded-2xl overflow-hidden group flex flex-col hover:-translate-y-0.5 transition-transform">
                <div className="relative h-36 bg-gradient-to-br from-[#0E9AC8] to-[#0B5563] overflow-hidden">
                  {p.cover_url ? <img src={mediaFull(p.cover_url)} alt="" className="h-full w-full object-cover opacity-90 group-hover:scale-105 transition-transform" />
                    : <div className="h-full w-full flex items-center justify-center"><span className="font-display text-3xl font-extrabold text-white/90">{initials(p.company_name)}</span></div>}
                  {p.verified && <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 text-emerald-700 px-2.5 py-1 text-[11px] font-bold shadow"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
                  {p.review_count > 0 && <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/95 text-amber-600 px-2 py-1 text-[11px] font-bold shadow"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {p.rating}</span>}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">{p.company_name}</h3>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5 text-[#0E9AC8]" />{(p.categories || [])[0]}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#0E9AC8]" />{p.city || (p.service_cities || "").split(",")[0]}</span>
                  </div>
                  {p.about && <p className="mt-3 text-sm text-slate-500 line-clamp-2">{p.about}</p>}
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                    {p.experience_years ? <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{p.experience_years} yrs</span> : null}
                    {p.projects_handled ? <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{p.projects_handled} projects</span> : null}
                  </div>
                  <Button data-testid={`view-profile-${p.id}`} onClick={() => navigate(`/partner/${p.id}`)} className="mt-4 w-full rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4] mt-auto">View Profile <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
